import type { ObjectiveComponentDetail } from '@moment/horizon-data-access/proposal'
import type { ReferenceDataField } from '@moment/trading-types/instrument-data'
import type { MarketSideMetadata } from '@/types/api/live-inventory'
import type { SecurityValue } from './security-instrument-value'
import type { QuoteSideKey, SecurityRow } from './security-types'
import type { ObjectivePlan } from './translate-objective'

import { createLogger } from '@/lib/logger'

import { securityReadField } from './security-duration-column'
import { SECURITY_FIELD_BY_INSTRUMENT_FIELD } from './security-instrument-fields'
import { toInstrumentValue } from './security-instrument-value'
import { taxYieldColumn } from './security-tax-expressions'
import { bookFields, evalFields } from './security-wire-fields'
import { scoreObjectiveRow } from './translate-objective'

const logger = createLogger('shared/sorm/security/security-row-adapter')

/** Tax-aware yields of one side's top-of-book quote. */
export type QuoteYieldField =
  | `best_${QuoteSideKey}_post_tax_ytw`
  | `best_${QuoteSideKey}_tax_equivalent_yield`

const QUOTE_YIELD_FIELDS: ReadonlyArray<
  readonly [QuoteYieldField, QuoteSideKey, 'postTaxYtw' | 'taxEquivalentYield']
> = [
  ['best_ask_post_tax_ytw', 'ask', 'postTaxYtw'],
  ['best_ask_tax_equivalent_yield', 'ask', 'taxEquivalentYield'],
  ['best_bid_post_tax_ytw', 'bid', 'postTaxYtw'],
  ['best_bid_tax_equivalent_yield', 'bid', 'taxEquivalentYield'],
]

// Returns the adapter fills without a field pair.
const ADAPTED_RETURNS = new Set([
  'asks',
  'bids',
  'filter_lists',
  'objective_score',
  'objective_score_decomposition',
])

/** A live-inventory field and the Security field or tax column that serves it. */
export type InstrumentFieldPair = readonly [
  ProjectedField | QuoteYieldField,
  string,
]

/** Instrument fields every adapted row carries: the id and the evals the limits fall back to. */
const ALWAYS_RETURNED: ReadonlyArray<ReferenceDataField> = [
  'isin',
  'bid_price',
  'bid_ytw',
  'bid_ytm',
  'ask_price',
  'ask_ytw',
  'ask_ytm',
  'eval_price',
  'eval_ytw',
  'eval_ytm',
]

function isProjectedField(field: ReferenceDataField): field is ProjectedField {
  return (
    field !== 'filter_lists' &&
    field !== 'objective_score' &&
    field !== 'objective_score_decomposition'
  )
}

/**
 * The requested live-inventory returns that have a Security field or tax
 * column, warning about any that have neither. Quote yields read the quote
 * `topOfBookLevel` picks: the MPE for evals-as-orders orgs, the post-fee book
 * otherwise.
 */
export function instrumentFieldPairs(
  returns: ReadonlyArray<string>,
  useEvalsAsOrders: boolean,
): InstrumentFieldPair[] {
  const requested = new Set<string>([...ALWAYS_RETURNED, ...returns])
  const pairs: InstrumentFieldPair[] = []
  for (const instrumentField of SECURITY_FIELD_BY_INSTRUMENT_FIELD.keys()) {
    if (!requested.has(instrumentField)) continue
    const securityField = securityReadField(instrumentField)
    if (securityField && isProjectedField(instrumentField)) {
      pairs.push([instrumentField, securityField])
    }
  }
  for (const [field] of QUOTE_YIELD_FIELDS) {
    if (!requested.has(field)) continue
    const column = quoteYieldTaxColumn(field, useEvalsAsOrders)
    if (column) pairs.push([field, column])
  }
  const paired = new Set<string>(pairs.map(([field]) => field))
  const unmapped = [...requested].filter(
    (field) => !paired.has(field) && !ADAPTED_RETURNS.has(field),
  )
  if (unmapped.length > 0) {
    logger.warn('Requested live-inventory fields have no Security mapping', {
      fields: unmapped,
    })
  }
  return pairs
}

/** The tax computed column serving a live-inventory quote yield such as `best_ask_post_tax_ytw`. */
export function quoteYieldTaxColumn(
  field: string,
  useEvalsAsOrders: boolean,
): string | undefined {
  const entry = QUOTE_YIELD_FIELDS.find(([quoteYield]) => quoteYield === field)
  if (!entry) return undefined
  const [, side, kind] = entry
  return taxYieldColumn(useEvalsAsOrders ? 'mpe' : 'best', side, kind)
}

function isQuoteYieldField(field: string): field is QuoteYieldField {
  return QUOTE_YIELD_FIELDS.some(([quoteField]) => quoteField === field)
}

export interface ToSecurityInventoryItemOptions {
  fieldPairs: ReadonlyArray<InstrumentFieldPair>
  /** Security fields SORM serializes as decimal strings. */
  decimalFields: ReadonlySet<string>
  useEvalsAsOrders: boolean
  objective?: ObjectivePlan
  /** Whether `objective_score` was requested; without an objective it scores zero, as live inventory does. */
  scoresObjective?: boolean
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** A Security row value as a scalar; sets and objects are not instrument fields. */
function scalar(value: unknown): SecurityValue {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }
  return null
}

type ProjectedField = Exclude<
  ReferenceDataField,
  'filter_lists' | 'objective_score' | 'objective_score_decomposition'
>

/**
 * A Security row projected onto live-inventory field names. Like a live
 * inventory response it carries only the requested fields, so each is
 * optional and typed as the scalar SORM sent.
 */
export type SecurityInventoryItem = Partial<
  Record<ProjectedField | QuoteYieldField, SecurityValue>
> & {
  asks: MarketSideMetadata[]
  bids: MarketSideMetadata[]
  filter_lists?: string[]
  objective_score?: number | null
  objective_score_decomposition?: ObjectiveComponentDetail[]
}

/**
 * One side's top of book from a Security row. Evals-as-orders orgs quote the
 * side's MPE with no fee; everyone else gets the post-fee best quote, or no
 * level when the book is empty, so MPE never poses as a quote.
 */
export function topOfBookLevel(
  row: SecurityRow,
  side: QuoteSideKey,
  useEvalsAsOrders: boolean,
): MarketSideMetadata | null {
  const book = bookFields(side)
  const quantity = toNumber(row[book.quantity]) ?? 0
  const minQuantity = toNumber(row[book.minQuantity]) ?? 0
  const minIncrement = toNumber(row[book.minIncrement]) ?? undefined

  if (useEvalsAsOrders) {
    const evals = evalFields(side)
    const price = toNumber(row[evals.price])
    if (price === null) return null
    const ytw = toNumber(row[evals.ytw]) ?? 0
    const ytm = toNumber(row[evals.ytm]) ?? undefined
    return {
      price,
      yield_to_worst: ytw,
      yield_to_maturity: ytm,
      pre_fee_price: price,
      pre_fee_yield_to_worst: ytw,
      pre_fee_yield_to_maturity: ytm,
      effective_markup: 0,
      quantity,
      min_quantity: minQuantity,
      min_increment: minIncrement,
    }
  }

  const price = toNumber(row[book.price])
  if (price === null) return null
  return {
    price,
    yield_to_worst: toNumber(row[book.ytw]) ?? 0,
    yield_to_maturity: toNumber(row[book.ytm]) ?? undefined,
    pre_fee_price: toNumber(row[book.preFeePrice]) ?? undefined,
    pre_fee_yield_to_worst: toNumber(row[book.preFeeYtw]) ?? undefined,
    pre_fee_yield_to_maturity: toNumber(row[book.preFeeYtm]) ?? undefined,
    effective_markup: toNumber(row[book.effectiveMarkup]) ?? undefined,
    quantity,
    min_quantity: minQuantity,
    min_increment: minIncrement,
  }
}

/**
 * A Security row as a live-inventory-shaped item: requested fields renamed,
 * decimals as numbers, `asks`/`bids` synthesized from the top of book, and the
 * objective score with its decomposition when an objective was requested.
 */
export function toSecurityInventoryItem(
  row: SecurityRow,
  options: ToSecurityInventoryItemOptions,
): SecurityInventoryItem {
  const ask = topOfBookLevel(row, 'ask', options.useEvalsAsOrders)
  const bid = topOfBookLevel(row, 'bid', options.useEvalsAsOrders)
  const item: SecurityInventoryItem = {
    asks: ask ? [ask] : [],
    bids: bid ? [bid] : [],
  }
  for (const [instrumentField, securityField] of options.fieldPairs) {
    item[instrumentField] = isQuoteYieldField(instrumentField)
      ? toNumber(row[securityField])
      : toInstrumentValue({
          instrumentField,
          value: scalar(row[securityField]),
          isDecimal: options.decimalFields.has(securityField),
        })
  }
  if (options.objective) {
    Object.assign(item, scoreObjectiveRow(options.objective, row))
  } else if (options.scoresObjective) {
    item.objective_score = 0
    item.objective_score_decomposition = []
  }
  return item
}

/** Whether an adapted item carries a quote on either side. */
export function hasTopOfBook(
  item: Pick<SecurityInventoryItem, 'asks' | 'bids'>,
): boolean {
  return item.asks.length > 0 || item.bids.length > 0
}
